import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import useSplitChunks from './helpers/useSplitChunks';
import { refMapper } from 'services';

import classes from './ContentSubpages.module.scss';

const getColumnCount = w => {
  if (!w) return 3;
  if (w < 520) return 1;
  if (w < 900) return 2;
  if (w < 1200) return 3;
  if (w < 1500) return 4;
  return 5;
};

const distributeIntoColumns = (chunks, columnCount) => {
  const cols = Array.from({ length: columnCount }, () => ({
    items: [],
    size: 0
  }));

  for (const block of chunks) {
    const blockSize = 1 + (block?.content?.length ?? 0);

    let bestIndex = 0;
    for (let i = 1; i < cols.length; i++) {
      if (cols[i].size < cols[bestIndex].size) bestIndex = i;
    }

    cols[bestIndex].items.push(block);
    cols[bestIndex].size += blockSize;
  }

  return cols.map(c => c.items);
};

const ContentSubpages = ({ contentSubpages, parent }) => {
  const { bu, lang, section } = useParams();

  const navLinkRefsByName = useRef(new Map());
  const setNavLinkRef = useCallback((subpageName, node) => {
    if (!subpageName) return;
    if (node) navLinkRefsByName.current.set(subpageName, node);
    else navLinkRefsByName.current.delete(subpageName);
  }, []);

  const chunks = useSplitChunks(contentSubpages);

  const [columnCount, setColumnCount] = useState(() =>
    typeof window !== 'undefined' ? getColumnCount(window.innerWidth) : 3
  );

  useEffect(() => {
    const onResize = () => setColumnCount(getColumnCount(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const letters = useMemo(() => (chunks ?? []).map(c => c.letter), [chunks]);

  const columns = useMemo(() => {
    if (!chunks || chunks.length === 0) return [];
    return distributeIntoColumns(chunks, columnCount);
  }, [chunks, columnCount]);

  useEffect(() => {
    if (!parent?.name) return;

    for (const [subpageName, node] of navLinkRefsByName.current.entries()) {
      if (!node) continue;
      refMapper.setSubContentRef(parent.name, subpageName, node);
    }
  }, [parent?.name, section, chunks]);

  if (!chunks || chunks.length === 0) return null;

  const topTitle = parent?.title ?? parent?.name ?? '';

  return (
    <div className={classes.Wrapper}>
      <div className={classes.TopBar}>
        <h2 className={classes.TopBarTitle}>{topTitle}</h2>
      </div>

      <nav className={classes.AlphaBar} aria-label="Alphabet navigation">
        <ul className={classes.AlphaList}>
          {letters.map(letter => (
            <li key={letter} className={classes.AlphaItem}>
              <a
                className={classes.AlphaLink}
                href={`#letter-${encodeURIComponent(letter)}`}
              >
                {letter}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div
        className={classes.TableMasonry}
        // eslint-disable-next-line no-useless-computed-key
        style={{ ['--cols']: columnCount }}
        role="list"
      >
        {columns.map((colBlocks, colIndex) => (
          <div
            key={`col-${colIndex}`}
            className={classes.TableColumn}
            role="listitem"
          >
            {colBlocks.map(({ letter, content }) => (
              <section
                key={letter}
                id={`letter-${encodeURIComponent(letter)}`}
                className={classes.Block}
                aria-label={`Section ${letter}`}
              >
                <div className={classes.BlockHeader}>{letter}</div>

                <ul className={classes.BlockList}>
                  {content.map(({ id, name, title }) => (
                    <li key={id ?? name} className={classes.BlockItem}>
                      <NavLink
                        activeClassName={classes.ActiveLink}
                        ref={node => setNavLinkRef(name, node)}
                        className={classes.ItemLink}
                        to={`/${bu}/${name}/${lang}`}
                        title={title}
                      >
                        {title}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default memo(ContentSubpages);
